import { useEffect } from 'react';
import { NextPage } from 'next';
import { triggerTracking } from '@zoopla/tracking';
import { useRouter } from 'next/router';
import { SearchResults } from '@zoopla/listing-search/results';
import { graphQLWrapper } from '@zoopla/apollo';
import { SearchResultsLayout } from '@zoopla/listing-search/layout';
import type { Listing } from '@zoopla/listing-search-queries';
import { useClientSideRoutingLoading } from '@zoopla/listing-search/use-load-clientside';
import {
    SearchProvider,
    ServerData,
    SearchContextType,
    SeoBlurb,
    SearchResultMeta
} from '@zoopla/listing-search-context';
import { Links } from '@zoopla/listing-search-context';
import { UserTaxonomy } from '@zoopla/tracking';
import { internalPromoIds } from '@zoopla/listing-search/featured/properties';
import { FormattedListing } from '@zoopla/listing-search-utils';
import { InformationLayout } from '@zoopla/layout/page';
import Head from 'next/head';
import { Sprite } from '@zoopla/sprite';
import sprite from './critical.sprite';
import { useBreakpointListener } from '@zoopla/dom-util';
import { useLazyQualifiedOptimizelyExperiment } from '@zoopla/optimizely';
import { queries } from '@zoopla-eng/zds-components/themes/queries';

type InternalPromoId = (typeof internalPromoIds)[number];
type PromoIds = (typeof internalPromoIds)[number];

type Product =
    | {
          listingId: string;
          featuredType?: string | null;
          title?: string | null;
          price?: string | null;
          alternativeRentFrequencyLabel?: string | null;
          address?: string | null;
          branch?: { logoUrl?: string | null; name?: string | null } | null;
          listingUris?: {
              detail?: string | null;
          } | null;
          features?:
              | ({
                    iconId?: string | null;
                    content?: number | null;
                } | null)[]
              | null;
          image?: {
              src?: string | null;
              responsiveImgList?:
                  | ({ src?: string | null; width?: number | null } | null)[]
                  | null;
          } | null;
      }
    | InternalPromoId;

type Gallery = [caption: string | null, uri: string][];

type CompressedFormattedListing = Omit<
    FormattedListing<Listing>,
    'otherPropertyImages'
> & { gallery?: Gallery };

export type SearchPageProps = {
    searchResultsMeta?: SearchResultMeta;
    breadcrumbs?: ServerData['breadcrumbs'];
    initialState: SearchContextType;
    regularListingsFormatted?: CompressedFormattedListing[];
    extendedListingsFormatted?: CompressedFormattedListing[];
    featuredProperties?: (Product | PromoIds)[];
    transactionType?: SeoBlurb['transactionType'];
    error?: { message: string };
    links?: Links;
    userTaxonomy?: UserTaxonomy;
    isLoading?: boolean;
};

const SearchPage: NextPage<SearchPageProps> = (pageProps) => {
    // This used to trigger the GA event and pass the variant. GTM wll handle showing the Banner
    const isMobile = useBreakpointListener(queries.S[200], 'LT');
    useLazyQualifiedOptimizelyExperiment(
        'mobile_app_banner_test_v2_lsrp',
        isMobile
    );

    const clientRoutingLoading = useClientSideRoutingLoading();
    const router = useRouter();

    const {
        initialState,
        regularListingsFormatted,
        extendedListingsFormatted,
        featuredProperties,
        transactionType,
        searchResultsMeta,
        breadcrumbs,
        links,
        error
    } = pageProps ?? {};

    useEffect(() => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'optimizely_feature',
            eventAction: 'show_listings_halo',
            eventLabel: 'original'
        });
    }, []);

    const { query } = router;

    const { dfpAdTargeting, seoAccordions } = initialState ?? {};

    const currentPage =
        typeof query['pn'] === 'string' ? parseInt(query['pn']) : 1;

    const numberOfRegularListings =
        regularListingsFormatted && regularListingsFormatted.length
            ? regularListingsFormatted.length
            : 0;

    return (
        <>
            <Head>
                <link href="https://st.zoocdn.com" rel="preconnect" />
                <link href="https://api.segment.io" rel="preconnect" />,
                <link
                    href="https://securepubads.g.doubleclick.net"
                    rel="preconnect"
                />
            </Head>
            <Sprite content={sprite} />
            <InformationLayout
                main={
                    <SearchProvider initialState={initialState}>
                        <SearchResultsLayout
                            dfpAdTargeting={dfpAdTargeting ?? {}}
                            seoAccordions={seoAccordions ?? []}
                            numberOfRegularListings={numberOfRegularListings}
                            pageNumber={currentPage}
                            links={links as Links}
                            analyticsTaxonomy={
                                searchResultsMeta?.analyticsTaxonomy
                            }
                        >
                            <SearchResults
                                regularListings={regularListingsFormatted ?? []}
                                extendedListings={
                                    extendedListingsFormatted ?? []
                                }
                                featuredProperties={featuredProperties ?? []}
                                canonicalPath={
                                    searchResultsMeta?.metaInfo.canonicalUri ||
                                    ''
                                }
                                searchResults={searchResultsMeta}
                                breadcrumbs={breadcrumbs}
                                transactionType={transactionType ?? 'sale'}
                                loading={
                                    clientRoutingLoading ||
                                    pageProps.isLoading === true
                                }
                                error={error}
                                links={links as Links}
                            />
                        </SearchResultsLayout>
                    </SearchProvider>
                }
            />
        </>
    );
};

// SearchPage exported here just for testing
export { SearchPage };

const SearchPageWithApollo = graphQLWrapper(SearchPage);
export default SearchPageWithApollo;
